import { Card, Typography, Chip, Stack, Button, Collapse } from "@mui/material";
import { Box } from "@mui/system";
import {
  colors,
  spacing,
  typography,
} from "../../../../../../../../../assets/styles/tokens";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import React from "react";

import MissingDataCardItemMoreMetrics from "./MissingDataCardItemMoreMetrics";
import {
  CURRENCY_SYMBOLS,
  cleanNumber,
  formatCurrency,
  formatInt,
  getPercentStyle,
} from "../../../../../../DashboardDesktop/Anomalies/AnomaliesTabs/Utils/anomaliesColumns";

// ─────────────────────────────────────────────────────────────────────────────

interface MissingDataCardItemViewProps {
  row: Record<string, any>;
}

// FTD/QFTD count + coloured %CVR badge (same rule as Desktop Anomalies:
// 0-12% red, >12-20% yellow, >20% green).
const PercentBadge = ({
  count,
  percent,
}: {
  count: number;
  percent: number;
}) => {
  const style = getPercentStyle(percent);
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
      }}
    >
      <Typography
        sx={{
          color: colors.GrayLightMode[900].$value,
          fontSize: typography.Size.Text.sm.$value,
          fontWeight: typography.Font_weight.Semibold.$value,
          pr: spacing.Spacing.xs.$value,
        }}
      >
        {formatInt(count)}
      </Typography>
      <Chip
        label={`${percent.toFixed(1)}%`}
        size="small"
        sx={{
          borderRadius: spacing.Radius.xs.$value,
          fontSize: typography.Size.Text.sm.$value,
          mr: spacing.Spacing.sm.$value,
          bgcolor: style.bg,
          color: style.color,
          fontWeight: typography.Font_weight.Semibold.$value,
        }}
      />
    </Box>
  );
};

export function MissingDataCardItemView({ row }: MissingDataCardItemViewProps) {
  const [open, setOpen] = React.useState(false);

  const cpaSymbol = CURRENCY_SYMBOLS[row.cpa_currency_code] ?? "";
  const rsSymbol = CURRENCY_SYMBOLS[row.revshare_currency_code] ?? "";

  return (
    <Card
      sx={{
        p: 1,
        borderRadius: spacing.Radius.lg.$value,
        boxShadow: "0px 1px 2px #0A0D120F , 0px 1px 3px #0A0D121A",
        border: "1px solid" + colors.GrayLightMode[200].$value,
        mb: spacing.Spacing.none.$value,
        mt: spacing.Spacing.xl.$value + "!important",
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography
          variant="body1"
          sx={{
            color: colors.GrayLightMode[700].$value,
            fontSize: typography.Size.Text.xs.$value,
            fontWeight: typography.Font_weight.Medium.$value,
          }}
        >
          {row.brand_display_name} ({row.brand_id})
        </Typography>
      </Stack>

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        mt={spacing.Spacing.md.$value}
        mb={spacing.Spacing.xs.$value}
      >
        <Box display="flex" alignItems="center">
          <Typography
            variant="body2"
            sx={{
              color: colors.GrayLightMode[900].$value,
              fontWeight: typography.Font_weight.Bold.$value,
              mr: spacing.Spacing.xs.$value,
            }}
          >
            {formatInt(row.clicks)}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: colors.GrayLightMode[900].$value,
              mr: spacing.Spacing.xs.$value,
            }}
          >
            Click out, of which:
          </Typography>
        </Box>
      </Stack>

      <Stack
        direction="row"
        justifyContent="space-between"
        mt={spacing.Spacing.md.$value}
        mb={spacing.Spacing.md.$value}
      >
        <Box
          sx={{
            width: "50%",
            pr: spacing.Spacing.md.$value,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography
            sx={{
              color: colors.GrayLightMode[500].$value,
              fontSize: typography.Size.Text.xs.$value,
            }}
          >
            FTD
          </Typography>
          <PercentBadge count={row.ftd} percent={cleanNumber(row.FTD_perc)} />
        </Box>
        <Box
          sx={{
            width: "50%",
            borderLeft: "1px solid" + colors.GrayLightMode[200].$value,
            pl: spacing.Spacing.md.$value,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography
            sx={{
              color: colors.GrayLightMode[500].$value,
              fontSize: typography.Size.Text.xs.$value,
            }}
          >
            QFTD
          </Typography>
          <PercentBadge count={row.qftd} percent={cleanNumber(row.QFTD_perc)} />
        </Box>
      </Stack>

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        mt={spacing.Spacing["2xl"].$value}
        mb={spacing.Spacing.xs.$value}
      >
        <Box display="flex" alignItems="center">
          <Typography
            variant="body2"
            sx={{
              color: colors.GrayLightMode[900].$value,
              mr: spacing.Spacing.xs.$value,
            }}
          >
            Total Income
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: colors.GrayLightMode[900].$value,
              fontWeight: typography.Font_weight.Bold.$value,
              mr: spacing.Spacing.xs.$value,
            }}
          >
            {formatCurrency(row.totalincome, cpaSymbol)}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: colors.GrayLightMode[900].$value,
              mr: spacing.Spacing.xs.$value,
            }}
          >
            , of which:
          </Typography>
        </Box>
      </Stack>

      <Stack
        direction="row"
        justifyContent="space-between"
        mt={spacing.Spacing.md.$value}
        mb={spacing.Spacing.md.$value}
      >
        <Box
          sx={{
            width: "50%",
            pr: spacing.Spacing.md.$value,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography
            sx={{
              color: colors.GrayLightMode[500].$value,
              fontSize: typography.Size.Text.xs.$value,
            }}
          >
            CPA
          </Typography>
          <Typography
            sx={{
              color: colors.GrayLightMode[900].$value,
              fontSize: typography.Size.Text.sm.$value,
              fontWeight: typography.Font_weight.Semibold.$value,
              pr: spacing.Spacing.xs.$value,
            }}
          >
            {formatCurrency(row.cpa_income, cpaSymbol)}
          </Typography>
        </Box>
        <Box
          sx={{
            width: "50%",
            borderLeft: "1px solid" + colors.GrayLightMode[200].$value,
            pl: spacing.Spacing.md.$value,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography
            sx={{
              color: colors.GrayLightMode[500].$value,
              fontSize: typography.Size.Text.xs.$value,
            }}
          >
            RS
          </Typography>
          <Typography
            sx={{
              color: colors.GrayLightMode[900].$value,
              fontSize: typography.Size.Text.sm.$value,
              fontWeight: typography.Font_weight.Semibold.$value,
              pr: spacing.Spacing.xs.$value,
            }}
          >
            {formatCurrency(row.revshare_income, rsSymbol)}
          </Typography>
        </Box>
      </Stack>

      <Stack direction="row" alignItems="center" justifyContent="center">
        <Button
          size="small"
          onClick={() => setOpen(!open)}
          sx={{
            padding: "0",
            fontSize: typography.Size.Text.xs.$value,
            lineHeight: typography.Line_height.text.xs.$value,
            color: colors.Brand[800].$value,
            fontWeight: typography.Font_weight.Regular.$value,
            textTransform: "capitalize",
            letterSpacing: "0px",
          }}
        >
          {open ? "Show Less Metrics" : "Show More Metrics"}
          <ExpandMoreIcon
            sx={{
              color: colors.Brand[800].$value,
              transform: open ? "rotate(180deg)" : "rotate(0deg)",
              transition: "0.2s",
            }}
          />
        </Button>
      </Stack>
      <Stack>
        <Collapse in={open}>
          <MissingDataCardItemMoreMetrics row={row} />
        </Collapse>
      </Stack>
    </Card>
  );
}
export default MissingDataCardItemView;
